import React, { useState, useEffect, useMemo } from "react";
import Select from "react-select";
import { motion, AnimatePresence } from "framer-motion";
import { useDebounce } from "use-debounce";
import {
  Search,
  RotateCw,
  SlidersHorizontal,
  Plus,
  Eraser,
  Check,
} from "lucide-react";

import customSelectStyles from "../../../components/ui/customSelectStyles";
import MobileAdvancedFilterSheet from "../../../components/ui/MobileAdvancedFilterSheet";
import { hasPermission } from "../../../utils/permissionUtils";
import { getAllBranches } from "../../../api/hrm/branches/info";
import { getAllSalesEmployees } from "../../../api/hrm/employees/employeeInfo";

const TEXT = {
  searchPlaceholder: "Tìm theo từ khóa...",
  searchPlaceholderMobile: "Tìm khách, NV, nội dung…",
  refresh: "Làm mới",
  advanced: "Lọc nâng cao",
  advancedTitle: "Bộ lọc nâng cao",
  branch: "Chi nhánh",
  method: "Hình thức liên hệ",
  month: "Tháng liên hệ",
  contactDate: "Ngày liên hệ",
  staff: "Nhân viên ghi nhận",
  result: "Kết quả",
  followup: "Lịch hẹn gọi lại",
  allBranch: "Tất cả chi nhánh",
  allMethod: "Tất cả hình thức",
  allMonth: "Tất cả tháng",
  allContactDate: "Tất cả ngày",
  allStaff: "Tất cả nhân viên",
  allResult: "Tất cả kết quả",
  allFollowup: "Tất cả",
  clear: "Xóa lọc",
  apply: "Áp dụng",
  addContact: "Ghi nhận liên hệ",
};

const RESULT_OPTIONS = [
  { value: "Hẹn lại", label: "Hẹn lại" },
  { value: "Đạt", label: "Đạt" },
  { value: "Không phản hồi", label: "Không phản hồi" },
];

const FOLLOWUP_OPTIONS = [
  { value: "1", label: "Có lịch hẹn" },
  { value: "0", label: "Không có lịch hẹn" },
];

function currentMonthValue() {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  return `${y}-${m}`;
}

/** Mặc định: tháng liên hệ = tháng hiện tại */
export function getDefaultContactFilters() {
  return {
    keyword: "",
    branch_id: "",
    contact_method: "",
    month: currentMonthValue(),
    contact_date: "",
    staff_id: "",
    result: "",
    has_followup: "",
  };
}

const ContactFilter = ({
  branchOptions = [],
  contactMethodOptions = [],
  staffOptions: staffOptionsProp = [],
  onFilterChange,
  onNewClick,
  onRefresh,
  followupCount = 0,
  mobileTab = "followup",
  onMobileTabChange,
  showMobileFab = true,
}) => {
  const [advanced, setAdvanced] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [branchList, setBranchList] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [filters, setFilters] = useState(() => getDefaultContactFilters());
  const defaultSnapshot = useMemo(() => getDefaultContactFilters(), []);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const [debouncedKeyword] = useDebounce(filters.keyword, 400);

  useEffect(() => {
    getAllBranches()
      .then((res) => setBranchList(res?.items || []))
      .catch(() => setBranchList([]));
  }, []);

  useEffect(() => {
    // Chỉ tải 1 lần khi mount (tránh loop vì default prop `[]` đổi reference mỗi render)
    if (staffOptionsProp.length > 0) return undefined;

    let cancelled = false;
    getAllSalesEmployees()
      .then((rows) => {
        if (cancelled) return;
        const list = Array.isArray(rows) ? rows : rows?.items || rows?.data || [];
        setStaffList(
          list
            .map((e) => ({
              value: e.id || e.value,
              label: e.employee_name || e.name || e.label || "—",
            }))
            .filter((o) => o.value)
        );
      })
      .catch(() => {
        if (!cancelled) setStaffList([]);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only fetch
  }, []);

  const resolvedBranchOptions = useMemo(() => {
    if (branchOptions?.length) return branchOptions;
    return branchList.map((b) => ({ value: b.id, label: b.name }));
  }, [branchOptions, branchList]);

  const resolvedMethodOptions = useMemo(() => {
    if (contactMethodOptions?.length) return contactMethodOptions;
    return [
      { value: "Gọi điện", label: "Gọi điện" },
      { value: "Email", label: "Email" },
      { value: "Gặp trực tiếp", label: "Gặp trực tiếp" },
      { value: "Zalo", label: "Zalo" },
    ];
  }, [contactMethodOptions]);

  const resolvedStaffOptions = useMemo(() => {
    if (staffOptionsProp?.length) return staffOptionsProp;
    return staffList;
  }, [staffOptionsProp, staffList]);

  useEffect(() => {
    onFilterChange?.({ ...filters, keyword: debouncedKeyword });
  }, [
    debouncedKeyword,
    filters.branch_id,
    filters.contact_method,
    filters.month,
    filters.contact_date,
    filters.staff_id,
    filters.result,
    filters.has_followup,
  ]);

  const updateFilter = (name, value) => {
    const updated = { ...filters, [name]: value };
    // Tháng / ngày cụ thể: chọn cái này thì bỏ cái kia để tránh chồng điều kiện
    if (name === "month" && value) updated.contact_date = "";
    if (name === "contact_date" && value) updated.month = "";
    setFilters(updated);
    if (name !== "keyword") onFilterChange?.(updated);
  };

  const clearFilters = () => {
    const defaults = getDefaultContactFilters();
    setFilters(defaults);
    onFilterChange?.(defaults);
  };

  const handleRefresh = () => {
    onRefresh?.();
    onFilterChange?.(filters);
  };

  const advancedKeys = [
    "branch_id",
    "contact_method",
    "contact_date",
    "staff_id",
    "result",
    "has_followup",
  ];

  const isNonDefault = (key, value) => {
    if (key === "keyword") return Boolean(value);
    return String(value ?? "") !== String(defaultSnapshot[key] ?? "");
  };

  const isFiltering =
    advancedKeys.some((k) => isNonDefault(k, filters[k])) ||
    isNonDefault("month", filters.month) ||
    Boolean(filters.keyword);

  const activeFilterCount =
    advancedKeys.filter((k) => isNonDefault(k, filters[k])).length +
    (isNonDefault("month", filters.month) ? 1 : 0);

  const selectStyles = {
    ...customSelectStyles,
    control: (provided, state) => ({
      ...(typeof customSelectStyles.control === "function"
        ? customSelectStyles.control(provided, state)
        : provided),
      minHeight: 48,
      borderRadius: 16,
      borderColor: state.isFocused ? "#ef4444" : "#e5e7eb",
      boxShadow: state.isFocused ? "0 0 0 4px rgba(239, 68, 68, 0.05)" : "none",
    }),
    menuPortal: (base) => ({ ...base, zIndex: 999999 }),
  };

  const toolbarFieldClass =
    "h-11 rounded-2xl border border-gray-200 bg-gray-50 text-sm font-medium text-gray-700 outline-none transition-all focus:border-red-500 focus:bg-white focus:ring-0";

  const ActionButton = ({ icon: Icon, onClick, title, active = false }) => (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border transition-all duration-200 ${
        active
          ? "border-red-200 bg-red-50 text-red-600 shadow-sm"
          : "border-gray-200 bg-gray-50 text-gray-500 hover:border-red-300 hover:text-red-500"
      }`}
    >
      <Icon size={18} />
    </button>
  );

  const SelectField = ({ label, options, value, onChange, placeholder }) => (
    <div className="flex flex-col gap-1.5 w-full">
      <label className="text-[11px] font-bold text-gray-400 uppercase tracking-widest ml-1">
        {label}
      </label>
      <Select
        options={options}
        placeholder={placeholder}
        styles={selectStyles}
        value={options.find((x) => x.value === value) || null}
        onChange={(opt) => onChange(opt?.value || "")}
        isClearable
        menuPortalTarget={typeof document !== "undefined" ? document.body : null}
        menuPosition="fixed"
      />
    </div>
  );

  const MonthFieldCompact = ({ value, onChange, className = "" }) => (
    <input
      type="month"
      value={value || ""}
      onChange={(e) => onChange(e.target.value || "")}
      aria-label={TEXT.month}
      title={value ? `${TEXT.month}: ${value}` : TEXT.allMonth}
      className={`${toolbarFieldClass} px-3 [&::-webkit-calendar-picker-indicator]:cursor-pointer ${className}`}
    />
  );

  const DateField = ({ label, value, onChange, hint }) => (
    <div className="flex flex-col gap-1.5 w-full">
      <label className="ml-1 text-[11px] font-bold uppercase tracking-widest text-gray-400">
        {label}
      </label>
      <input
        type="date"
        value={value || ""}
        onChange={(e) => onChange(e.target.value || "")}
        aria-label={label}
        className="h-12 w-full rounded-2xl border-2 border-gray-200 bg-white px-3 text-sm text-gray-700 outline-none transition-all focus:border-red-500 focus:ring-4 focus:ring-red-500/5 [&::-webkit-calendar-picker-indicator]:cursor-pointer"
      />
      {!value && hint ? (
        <p className="ml-1 text-[10px] text-gray-400">{hint}</p>
      ) : null}
    </div>
  );

  const advancedFields = (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
      <SelectField
        label={TEXT.branch}
        options={resolvedBranchOptions}
        value={filters.branch_id}
        onChange={(v) => updateFilter("branch_id", v)}
        placeholder={TEXT.allBranch}
      />
      <SelectField
        label={TEXT.method}
        options={resolvedMethodOptions}
        value={filters.contact_method}
        onChange={(v) => updateFilter("contact_method", v)}
        placeholder={TEXT.allMethod}
      />
      <DateField
        label={TEXT.contactDate}
        value={filters.contact_date}
        onChange={(v) => updateFilter("contact_date", v)}
        hint={TEXT.allContactDate}
      />
      <SelectField
        label={TEXT.staff}
        options={resolvedStaffOptions}
        value={filters.staff_id}
        onChange={(v) => updateFilter("staff_id", v)}
        placeholder={TEXT.allStaff}
      />
      <SelectField
        label={TEXT.result}
        options={RESULT_OPTIONS}
        value={filters.result}
        onChange={(v) => updateFilter("result", v)}
        placeholder={TEXT.allResult}
      />
      <SelectField
        label={TEXT.followup}
        options={FOLLOWUP_OPTIONS}
        value={filters.has_followup}
        onChange={(v) => updateFilter("has_followup", v)}
        placeholder={TEXT.allFollowup}
      />
    </div>
  );

  const formatDateChip = (ymd) => {
    if (!ymd) return "";
    const [y, m, d] = String(ymd).split("-");
    if (!y || !m || !d) return ymd;
    return `${d}/${m}/${y}`;
  };

  /* ============================= MOBILE ============================= */
  if (isMobile) {
    return (
      <div className="mb-3" data-tour="contact-filter">
        <div className="relative mb-3 grid grid-cols-2 gap-1 rounded-2xl border border-gray-100 bg-white p-1 shadow-sm">
          <motion.div
            className="pointer-events-none absolute inset-y-1 w-[calc(50%-2px)] rounded-xl shadow-sm"
            initial={false}
            animate={{
              left: mobileTab === "followup" ? 4 : "calc(50% + 1px)",
              backgroundColor: mobileTab === "followup" ? "#fef3c7" : "#0f172a",
            }}
            transition={{ type: "spring", stiffness: 380, damping: 32 }}
          />
          <button
            type="button"
            onClick={() => onMobileTabChange?.("followup")}
            className={`relative z-10 rounded-xl px-3 py-2.5 text-xs font-black transition-colors duration-200 ${
              mobileTab === "followup" ? "text-amber-900" : "text-gray-500"
            }`}
          >
            Cần gọi lại
            {followupCount > 0 ? (
              <span className="ml-1 inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-red-700 px-1.5 py-0.5 text-[10px] text-white">
                {followupCount}
              </span>
            ) : null}
          </button>
          <button
            type="button"
            onClick={() => onMobileTabChange?.("all")}
            className={`relative z-10 rounded-xl px-3 py-2.5 text-xs font-black transition-colors duration-200 ${
              mobileTab === "all" ? "text-white" : "text-gray-500"
            }`}
          >
            Nhật ký
          </button>
        </div>

        <div className="mb-2">
          <MonthFieldCompact
            value={filters.month}
            onChange={(v) => updateFilter("month", v)}
            className="w-full"
          />
        </div>

        <div className="flex gap-2">
          <div className="relative flex-1 group">
            <Search
              className="absolute left-4 top-1/2 z-10 -translate-y-1/2 text-gray-400 group-focus-within:text-red-500 transition-colors"
              size={18}
            />
            <input
              type="search"
              inputMode="search"
              placeholder={TEXT.searchPlaceholderMobile}
              value={filters.keyword}
              onChange={(e) => updateFilter("keyword", e.target.value)}
              className={`${toolbarFieldClass} w-full pl-11 pr-4`}
            />
          </div>

          <ActionButton icon={RotateCw} onClick={handleRefresh} title={TEXT.refresh} />
          <button
            type="button"
            onClick={() => setAdvanced(true)}
            title={TEXT.advanced}
            className={`relative flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border transition-all duration-200 ${
              activeFilterCount > 0
                ? "border-red-200 bg-red-50 text-red-600 shadow-sm"
                : "border-gray-200 bg-gray-50 text-gray-500 hover:border-red-300 hover:text-red-500"
            }`}
          >
            <SlidersHorizontal size={18} />
            {activeFilterCount > 0 ? (
              <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-700 px-1 text-[10px] font-bold text-white">
                {activeFilterCount}
              </span>
            ) : null}
          </button>
        </div>

        {isFiltering ? (
          <div className="mt-2 flex flex-wrap gap-2">
            {filters.month ? (
              <span className="rounded-full border border-red-100 bg-red-50 px-2.5 py-1 text-[11px] font-bold text-red-700">
                Tháng {filters.month}
              </span>
            ) : null}
            {filters.contact_date ? (
              <span className="rounded-full border border-red-100 bg-red-50 px-2.5 py-1 text-[11px] font-bold text-red-700">
                Ngày {formatDateChip(filters.contact_date)}
              </span>
            ) : null}
            {filters.branch_id ? (
              <span className="rounded-full border border-gray-200 bg-gray-50 px-2.5 py-1 text-[11px] font-bold text-gray-600">
                {resolvedBranchOptions.find((o) => o.value === filters.branch_id)
                  ?.label || "Chi nhánh"}
              </span>
            ) : null}
            {filters.contact_method ? (
              <span className="rounded-full border border-gray-200 bg-gray-50 px-2.5 py-1 text-[11px] font-bold text-gray-600">
                {filters.contact_method}
              </span>
            ) : null}
            {filters.staff_id ? (
              <span className="rounded-full border border-gray-200 bg-gray-50 px-2.5 py-1 text-[11px] font-bold text-gray-600">
                {resolvedStaffOptions.find((o) => o.value === filters.staff_id)
                  ?.label || "NV"}
              </span>
            ) : null}
            {filters.result ? (
              <span className="rounded-full border border-gray-200 bg-gray-50 px-2.5 py-1 text-[11px] font-bold text-gray-600">
                {filters.result}
              </span>
            ) : null}
            {filters.has_followup ? (
              <span className="rounded-full border border-gray-200 bg-gray-50 px-2.5 py-1 text-[11px] font-bold text-gray-600">
                {FOLLOWUP_OPTIONS.find((o) => o.value === filters.has_followup)
                  ?.label || "Lịch hẹn"}
              </span>
            ) : null}
            <button
              type="button"
              onClick={clearFilters}
              className="rounded-full px-2.5 py-1 text-[11px] font-bold text-red-600"
            >
              Xóa lọc
            </button>
          </div>
        ) : null}

        {hasPermission("crm.contact_history", "C") && showMobileFab && !advanced && (
          <button
            type="button"
            onClick={onNewClick}
            className="fixed bottom-20 right-5 z-40 flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-r from-red-700 to-red-800 text-white shadow-lg shadow-red-200 active:scale-95"
            title={TEXT.addContact}
            aria-label={TEXT.addContact}
          >
            <Plus size={22} />
          </button>
        )}

        <MobileAdvancedFilterSheet
          open={advanced}
          onClose={() => setAdvanced(false)}
          title={TEXT.advancedTitle}
          footer={
            <>
              {isFiltering ? (
                <button
                  type="button"
                  onClick={() => {
                    clearFilters();
                    setAdvanced(false);
                  }}
                  className="flex-1 rounded-2xl border border-gray-300 py-4 font-bold text-gray-600 hover:bg-gray-50"
                >
                  {TEXT.clear}
                </button>
              ) : null}
              <button
                type="button"
                onClick={() => setAdvanced(false)}
                className="flex flex-1 items-center justify-center gap-2 rounded-2xl bg-red-600 py-4 font-bold text-white shadow-lg shadow-red-200"
              >
                <Check size={18} />
                {TEXT.apply}
              </button>
            </>
          }
        >
          {advancedFields}
        </MobileAdvancedFilterSheet>
      </div>
    );
  }

  /* ============================= DESKTOP ============================= */
  return (
    <div className="mb-6 rounded-xl border border-gray-100 bg-white p-5 shadow-sm" data-tour="contact-filter">
      <div className="flex items-center justify-between gap-4">
        <div className="flex flex-1 flex-wrap items-center gap-3">
          <MonthFieldCompact
            value={filters.month}
            onChange={(v) => updateFilter("month", v)}
            className="w-[168px] shrink-0"
          />

          <div className="relative min-w-[200px] max-w-sm flex-1 group">
            <Search
              className="absolute left-4 top-1/2 z-10 -translate-y-1/2 text-gray-400 group-focus-within:text-red-500 transition-colors"
              size={18}
            />
            <input
              type="text"
              placeholder={TEXT.searchPlaceholder}
              value={filters.keyword}
              onChange={(e) => updateFilter("keyword", e.target.value)}
              className={`${toolbarFieldClass} w-full pl-11 pr-4`}
            />
          </div>

          <ActionButton icon={RotateCw} onClick={handleRefresh} title={TEXT.refresh} />
          <ActionButton
            icon={SlidersHorizontal}
            onClick={() => setAdvanced(!advanced)}
            active={advanced || activeFilterCount > 0}
            title={TEXT.advanced}
          />

          {isFiltering && (
            <button
              type="button"
              onClick={clearFilters}
              className="ml-2 flex items-center gap-1 text-xs font-bold uppercase tracking-wider text-red-500 transition-all hover:text-red-700"
            >
              <Eraser size={14} /> {TEXT.clear}
            </button>
          )}
        </div>

        {hasPermission("crm.contact_history", "C") && (
          <button
            type="button"
            onClick={onNewClick}
            className="flex shrink-0 items-center gap-2 rounded-full bg-gradient-to-r from-red-700 to-red-800 px-6 py-2.5 text-xs font-bold uppercase tracking-wider text-white transition-all hover:shadow-lg hover:shadow-red-100 active:scale-95"
          >
            <Plus size={18} />
            {TEXT.addContact}
          </button>
        )}
      </div>

      <AnimatePresence>
        {advanced && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden"
          >
            <div className="mt-6 border-t border-gray-100 pt-6">{advancedFields}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ContactFilter;
